import {
  isDeniedPtmApplication,
  isDraftPtmApplication,
  shouldReplaceApplyCtaWithStatus,
} from "./prospect/ptmApplicationStatus"

/**
 * Presentation paths for Associate/Affiliate reapply, driven by Coach Tools
 * eligibility + latest PTM application status. Shared by TerminatedModal,
 * Reapply Path Offer surfaces, and Course Curriculum Become/Resume Associate.
 */
export const REAPPLY_PATH = {
  APPLY: "apply",
  RESUME: "resume",
  STATUS: "status",
  DENIED: "denied",
  UNAVAILABLE: "unavailable",
}

/**
 * Per-path action state (Associate or Affiliate).
 * Denied keeps Apply when eligible — backend does not treat denied as blocking.
 * Draft shows Resume even when eligibility is false (in-progress blocks new starts).
 *
 * @param {{ status?: string } | null | undefined} application
 * @param {boolean} eligibilityEligible
 */
export function reapplyPathActionState(application, eligibilityEligible) {
  const replaceApplyCta = shouldReplaceApplyCtaWithStatus(application?.status)
  const showDeniedStatus = isDeniedPtmApplication(application?.status)
  const isResume = isDraftPtmApplication(application?.status)
  const showApplyCta =
    !replaceApplyCta && (isResume || eligibilityEligible === true)
  const showStatus = replaceApplyCta || showDeniedStatus

  let path = REAPPLY_PATH.UNAVAILABLE
  if (replaceApplyCta) {
    path = REAPPLY_PATH.STATUS
  } else if (showDeniedStatus) {
    path = REAPPLY_PATH.DENIED
  } else if (isResume) {
    path = REAPPLY_PATH.RESUME
  } else if (showApplyCta) {
    path = REAPPLY_PATH.APPLY
  }

  return {
    path,
    showApplyCta,
    showStatus,
    showDeniedStatus,
    replaceApplyCta,
    isResume,
  }
}

/**
 * Combined Associate + Affiliate offer for Reapply Path Offer consumers.
 *
 * @param {object} input
 * @param {boolean} input.associateEligible
 * @param {boolean} input.affiliateEligible
 * @param {object | null} [input.associateApplication]
 * @param {object | null} [input.affiliateApplication]
 * @param {boolean} [input.applicationsReady=true]
 */
export function buildReapplyPathOffer({
  associateEligible,
  affiliateEligible,
  associateApplication = null,
  affiliateApplication = null,
  applicationsReady = true,
}) {
  if (!applicationsReady) {
    return {
      associate: null,
      affiliate: null,
      showAnyApplyCta: false,
      showAnyStatus: false,
      isUnavailable: false,
    }
  }

  const associate = reapplyPathActionState(
    associateApplication,
    associateEligible,
  )
  const affiliate = reapplyPathActionState(
    affiliateApplication,
    affiliateEligible,
  )
  const showAnyApplyCta = associate.showApplyCta || affiliate.showApplyCta
  const showAnyStatus = associate.showStatus || affiliate.showStatus

  return {
    associate,
    affiliate,
    showAnyApplyCta,
    showAnyStatus,
    isUnavailable: !showAnyApplyCta && !showAnyStatus,
  }
}

/**
 * Whether to load Coach Tools eligibility + PTM applications.
 * Skip only explicit Members (MB); Coach Tools also blocks Members server-side.
 */
export function shouldFetchReapplyPathEligibility({ programType }) {
  return programType !== "MB"
}

/**
 * Settled Coach Tools eligibility result for one path.
 */
export function isReapplyPathEligible({
  shouldFetch,
  loading,
  error,
  eligible,
}) {
  return !!shouldFetch && !loading && !error && eligible === true
}

/**
 * Live program type first; fall back to type-at-termination when auth `me`
 * omits it. AF normalizes to AP for Member-skip checks.
 */
export function resolvedProgramTypeForReapply(me) {
  const liveType = me?.mainProgramTypeCode || me?.type
  if (liveType) return liveType === "AF" ? "AP" : liveType

  const terminationType = me?.termination?.type
  return terminationType === "AF" ? "AP" : terminationType
}

/**
 * PTM applications query settled (or skipped).
 */
export function areReapplyPathApplicationsReady({
  shouldFetch,
  loading,
  data,
  error,
}) {
  return !shouldFetch || (!loading && !!(data || error))
}
